import CopyableField from '@src/components/atoms/copiableField';
import GovActionMetadataCopiableDescriptionField from '@src/components/atoms/metadataCopiableDescription';
import type { MetadataValidationResponse } from '@src/models/dtos/metadata';
import { GovernanceActionType } from '@src/models/enum';
import type { IDetails, IGovernanceAction } from '@src/models/types';
import { correctVoteAdaFormat } from '@src/utils/governanceActionUtils';
import { hexToBech32 } from '@src/utils/stringUtils';
import { isEmpty } from 'lodash';
import dynamic from 'next/dynamic';
import React from 'react';

// The markdown stack (react-markdown, remark, rehype, KaTeX) is the largest
// client dependency in the app. Loaded eagerly it made this the heaviest route
// in the build, for a renderer most visitors never scroll to and that most
// proposals do not need — the math plugins in particular fire only when the
// prose contains TeX. `ssr` stays at its default of true: the abstract,
// motivation and rationale are the page's indexable content, so they must be
// in the server-rendered HTML. This splits the client chunk only.
const MarkdownDescription = dynamic(() => import('./markdownDescription'), {
  loading: () => <div className="body14 text-gray-400">Loading…</div>,
});

interface ITreasuryDetails {
  'Reward Address': string;
  Amount: number;
}

const GovernanceActionReasoning = ({
  governanceAction,
  metadataResponse,
}: {
  governanceAction: IGovernanceAction;
  metadataResponse: MetadataValidationResponse | null;
}) => {
  const { proposal, createdAt } = governanceAction;
  const { index, tx } = createdAt;
  const cip129GovernanceAction = hexToBech32(
    `${tx}${index.toString(16).padStart(2, '0')}`,
    'gov_action'
  );

  // The indexer parsed this document when it indexed the proposal, so the
  // proposal response already carries the prose. Use it until the anchor
  // document has been fetched and validated — that round trip goes over IPFS
  // and used to be the reason the page showed nothing for seconds, or 404'd
  // when the gateway was down.
  const content = metadataResponse?.metadata ?? governanceAction.meta;

  const descriptions: IDetails[] = content
    ? [
        {
          label: 'Abstract',
          value: content.abstract || '',
        },
        {
          label: 'Motivation',
          value: content.motivation || '',
        },
        {
          label: 'Rationale',
          value: content.rationale || '',
        },
      ].filter((description) => !isEmpty(description.value))
    : [];

  const getTreasuryDetails = (detail: ITreasuryDetails) => {
    return (
      <>
        <div className="flex flex-col justify-between gap-1">
          <p className="body16 font-semibold dark:text-white">
            Receiving Address
          </p>

          <CopyableField
            key={detail['Reward Address']}
            value={detail['Reward Address']}
            className="gap-2"
            iconClassName="!h-[18px] !w-[18px]"
            textOnly
            textClassName="body14 text-gray-400 max-w-[210px] sm:max-w-[300px] xl:max-w-[500px]"
          />
        </div>
        <div className="flex flex-col justify-between gap-1">
          <p className="body16 font-semibold dark:text-white">Amount</p>

          <p className="body14 text-gray-400" key={detail.Amount}>
            {`₳ ${correctVoteAdaFormat(detail.Amount)}`}
          </p>
        </div>
      </>
    );
  };

  return (
    <React.Fragment>
      <GovActionMetadataCopiableDescriptionField
        label="Governance Action ID:"
        value={cip129GovernanceAction}
      />
      <GovActionMetadataCopiableDescriptionField
        label="Legacy Governance Action ID (CIP-105):"
        value={index ? `${tx}#${index}` : tx}
      />
      {descriptions.map((description) => (
        <MarkdownDescription
          key={description.label}
          label={description.label}
          value={description.value as string}
        />
      ))}
      {proposal.type === GovernanceActionType.TreasuryWithdrawal &&
        (Array.isArray(proposal.details)
          ? proposal.details.map((detail: ITreasuryDetails) => (
              <div key={detail['Reward Address']} className="flex gap-4">
                {getTreasuryDetails(detail)}
              </div>
            ))
          : getTreasuryDetails(proposal.details))}
    </React.Fragment>
  );
};

export default GovernanceActionReasoning;
